import { format, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'

/**
 * Display-only formatters for the admin UI.
 *
 * Why a centralised module
 * ------------------------
 * Every page that touches a mission shows hours, bonus minutes,
 * dates, money. Reproducing the formatting inline meant subtle
 * drift across the listing and the detail (the V1 had three
 * different "Xh:YY" implementations). One module, one source
 * of truth.
 *
 * Hours come back from the API as `string` because PostgreSQL
 * `numeric(6,2)` round-trips as a string in Drizzle. We accept
 * both `string` and `number` and coerce internally so callers
 * don't have to remember the convention at every call site.
 *
 * Money is also a string for the same reason. Null money fields
 * render as an em-dash so empty rows align with filled ones.
 */

/**
 * Decimal-hours → human-friendly "XhYY".
 *
 * Example:
 *   "8.25"  → "8h15"
 *   0.5     → "0h30"
 *   "10.00" → "10h00"
 *   "0"     → "0h00"
 *
 * Rounding: we floor minutes to avoid showing "5h60". Inputs
 * already come pre-rounded to the quarter by the engine, so any
 * remaining decimal noise is sub-minute and safe to drop.
 */
export function formatHours(value: string | number): string {
  const decimal = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(decimal)) return '0h00'
  const totalMinutes = Math.round(decimal * 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${hours}h${String(minutes).padStart(2, '0')}`
}

/**
 * Bonus minutes → "+XhYY" (with the leading sign), or empty
 * string when there's no bonus. Used in the table to keep the
 * column tidy when most missions have no bonus.
 */
export function formatBonusMinutes(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return ''
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `+${hours}h${String(mins).padStart(2, '0')}`
}

/**
 * Plain integer-minutes → "XhYY" (no sign). Used for individual
 * bonus lines on the detail page (lavage, déplacement).
 */
export function formatMinutes(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return '0h00'
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours}h${String(mins).padStart(2, '0')}`
}

/**
 * "YYYY-MM-DD" → "21/04/2026" (full, used in tooltips and as
 * fallbacks where space allows).
 */
export function formatDate(iso: string): string {
  return format(parseISO(iso), 'dd/MM/yyyy')
}

/**
 * "YYYY-MM-DD" → "21/04" (compact, used in dense table cells).
 * The table cell carries the long form as a `<title>` for
 * accessibility / hover.
 */
export function formatDateShort(iso: string): string {
  return format(parseISO(iso), 'dd/MM')
}

/**
 * "YYYY-MM-DD" → "Lundi 21 avril 2026" (verbose, used in page
 * titles and tooltips).
 */
export function formatDateLong(iso: string): string {
  const out = format(parseISO(iso), 'EEEE d MMMM yyyy', { locale: fr })
  return out.charAt(0).toUpperCase() + out.slice(1)
}

/**
 * ISO timestamp → "21/04/2026 14:32" (used for audit metadata
 * on the detail page).
 */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Money (EUR) — string `numeric(10,2)` → "15,00 €". `null`
 * renders as an em-dash so the column aligns with filled rows.
 *
 * Comma is the French decimal separator, with a non-breaking
 * space before the unit (typographic convention).
 */
export function formatMoney(amount: string | null | undefined): string {
  if (amount === null || amount === undefined || amount === '') return '—'
  const n = Number(amount)
  if (!Number.isFinite(n)) return '—'
  return `${n.toFixed(2).replace('.', ',')} €`
}

/**
 * Trim a "HH:MM:SS" timestamp from the API down to "HH:MM" for
 * display. Pass-through on already-trimmed values.
 */
export function trimSeconds(value: string): string {
  return value.length === 8 ? value.slice(0, 5) : value
}
